import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Flame, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * Premium dark admin login (Apple-grade luxury).
 * After successful login, routes to:
 *  - role admin       → /admin/command
 *  - role store_owner → /partner
 */
export const AdminLogin = () => {
    const navigate = useNavigate();
    const { setUserDirect } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email: email.trim(), password });
            localStorage.setItem("sh_token", data.token);
            setUserDirect?.(data.user);
            toast.success("Καλωσήρθες πίσω 👋");
            if (data.user.role === "admin") navigate("/admin/command");
            else if (data.user.role === "store_owner") navigate("/partner");
            else { setErr("Ο λογαριασμός δεν έχει δικαιώματα admin/partner"); }
        } catch (e2) {
            setErr(e2.response?.data?.detail || "Σφάλμα σύνδεσης");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="admin-login" className="burger-bg min-h-screen relative overflow-hidden grid lg:grid-cols-2">
            {/* Decorative side */}
            <div className="hidden lg:block relative">
                <img
                    src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=1400&q=80"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
                <div className="smoke-layer" />
                <div className="grain-layer" />
                <motion.div
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                    className="absolute bottom-12 left-12 right-12 z-10"
                >
                    <p className="font-display text-xs tracking-[0.5em] ember-text mb-3">// COMMAND CENTER</p>
                    <h1 className="font-display text-6xl leading-[0.95]">
                        FAST.<br />SECURE.<br /><span className="ember-text">RELENTLESS.</span>
                    </h1>
                    <p className="text-white/55 mt-4 max-w-md">
                        Ενιαία πλατφόρμα διαχείρισης για admins και partners. Live παραγγελίες, στατιστικά, menu CMS.
                    </p>
                </motion.div>
            </div>

            {/* Login form */}
            <div className="relative flex items-center justify-center px-6 sm:px-10 py-10">
                <div className="smoke-layer lg:hidden" />
                <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                    className="relative w-full max-w-md"
                >
                    <div className="flex items-center gap-2 mb-8">
                        <span className="h-10 w-10 grid place-items-center rounded-xl ember-gradient">
                            <Flame size={18} className="text-black" />
                        </span>
                        <div>
                            <p className="font-display text-2xl tracking-[0.18em]">GRILL BROS</p>
                            <p className="text-[10px] font-mono uppercase tracking-widest text-white/45">// admin · partner</p>
                        </div>
                    </div>

                    <h2 className="font-display text-5xl leading-[0.95] mb-2">SIGN IN.</h2>
                    <p className="text-sm text-white/55 mb-8">
                        Συνδέσου στο command center με τα admin/partner στοιχεία σου.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <FieldIcon icon={Mail} label="Email" testid="login-email" type="email" value={email} onChange={setEmail} placeholder="admin@yourstore.gr" />
                        <FieldIcon icon={Lock} label="Κωδικός" testid="login-password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />

                        {err && (
                            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="login-error" className="text-xs text-[var(--burger-orange-2)] text-center">
                                {err}
                            </motion.p>
                        )}

                        <motion.button
                            type="submit"
                            disabled={loading || !email || !password}
                            whileTap={{ scale: 0.98 }}
                            data-testid="login-submit"
                            className="magnet-cta primary w-full h-14 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {loading ? <Loader2 className="animate-spin" size={18} /> : <>Sign In <ArrowRight size={16} strokeWidth={3} /></>}
                        </motion.button>
                    </form>

                    <div className="mt-8 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/35">
                        <ShieldCheck size={11} /> JWT secured · role-based access
                    </div>

                    <div className="mt-10 text-center">
                        <button
                            onClick={() => navigate("/")}
                            className="text-[10px] font-display tracking-[0.32em] text-white/40 hover:text-[var(--burger-orange-2)] transition"
                        >
                            ← BACK TO STORE
                        </button>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

const FieldIcon = ({ icon: Icon, label, testid, type, value, onChange, placeholder }) => (
    <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">{label}</label>
        <div className="flex items-center gap-2 h-12 px-4 rounded-2xl bg-white/5 border border-white/10 focus-within:border-[var(--burger-orange)]/60 focus-within:bg-white/10 transition">
            <Icon size={15} className="text-white/45 shrink-0" />
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testid}
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/30"
            />
        </div>
    </div>
);

export default AdminLogin;
